import {
  faCheckCircle,
  faQuestionCircle,
  faTimesCircle,
} from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { Suspense } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useSearchParams } from 'react-router'
import { useMount } from 'react-use'
import invariant from 'tiny-invariant'

import CommentList from '@/components/misc/Comments/CommentList'
import CompanyProfileHeader from '@/components/misc/CompanyProfile/components/CompanyProfileHeader/CompanyProfileHeader'
import DevActions from '@/components/misc/CompanyProfile/components/DevActions'
import { COMPANY_PROFILE_TAB } from '@/components/misc/CompanyProfile/constants'
import useInitialReviewsQuery from '@/components/misc/CompanyProfile/hooks/useInitialReviewsQuery'
import CalendarLinksTab from '@/components/misc/CompanyProfile/tabs/CalendarLinksTab/CalendarLinksTab'
import CommercialAgreementsTab from '@/components/misc/CompanyProfile/tabs/CommercialAgreementsTab/CommercialAgreementsTab'
import MonitoringTab from '@/components/misc/CompanyProfile/tabs/MonitoringTab'
import OverviewTab from '@/components/misc/CompanyProfile/tabs/OverviewTab/OverviewTab'
import ReviewsTab from '@/components/misc/CompanyProfile/tabs/ReviewsTab/ReviewsTab'
import ServicesAndExpertise from '@/components/misc/CompanyProfile/tabs/ServicesAndExpertiseTab/ServicesAndExpertiseTab'
import TeamTab from '@/components/misc/CompanyProfile/tabs/TeamTab'
import {
  CompanyProfileTab,
  CompanyProfileTabInfo,
} from '@/components/misc/CompanyProfile/types'
import PageContainer from '@/components/misc/PageContainer'
import VideoEmbed from '@/components/misc/VideoEmbed'
import SkeletonBox from '@/components/skeleton/SkeletonBox'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import DevOnly from '@/components/utils/DevOnly'
import { useCompanyProfileQuery } from '@/hooks/useCompanyProfileQuery'
import {
  COMMENT_ENTITY_ALIAS,
  COMPANY_PROFILE_IDENTIFIER_TYPE,
  PANEL_AGREEMENT_MESSAGE,
  PANEL_STATUS,
  PENDING_PANEL_ACTION,
} from '@/lib/constants'
import { faExclamationTriangle } from '@/lib/fontAwesomePro/light'
import { ROUTES } from '@/lib/routes'
import { SEARCH_PARAMS } from '@/lib/searchParams'
import companyProfileService from '@/lib/services/companyProfileService'
import { cn } from '@/lib/utils'

type Props = Pick<
  typeof SEARCH_PARAMS.companyProfile,
  'identifier' | 'identifierType'
>

const CompanyProfile: React.FC<Props> = ({ identifier, identifierType }) => {
  const isFcaFirm = identifierType === COMPANY_PROFILE_IDENTIFIER_TYPE.frn
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromSearchParam =
    searchParams.get(SEARCH_PARAMS.companyProfile.profileTab) || ''

  const companyProfileQuery = useCompanyProfileQuery({
    identifier,
    identifierType,
  })

  const companyProfile = companyProfileQuery.data
  const { company, panel, directory, companiesHouse } = companyProfile

  const initialReviewsQuery = useInitialReviewsQuery(company?.id)
  const totalReviewsCount = initialReviewsQuery?.data?.['hydra:totalItems'] ?? 0

  const shouldShowServicesAndExpertiseTab =
    companyProfile.isRegistered && companyProfile.hasServices

  const isInPanel =
    panel &&
    [PANEL_STATUS.approved, PANEL_STATUS.requested].includes(panel.status)

  function determineInitialTab(): CompanyProfileTab {
    if (companyProfileService.isValidTab(tabFromSearchParam)) {
      return tabFromSearchParam
    }

    if (isFcaFirm) {
      return COMPANY_PROFILE_TAB.overview
    }

    if (shouldShowServicesAndExpertiseTab) {
      return COMPANY_PROFILE_TAB.servicesAndExpertise
    }

    if (isInPanel) {
      return COMPANY_PROFILE_TAB.commercialAgreements
    }

    return COMPANY_PROFILE_TAB.overview
  }

  function buildTabs() {
    invariant(companyProfile, 'Expected companyProfile to be defined')
    const tabs: CompanyProfileTabInfo[] = []

    // Overview
    // ---------
    tabs.push({
      title: 'Overview',
      key: COMPANY_PROFILE_TAB.overview,
      content: <OverviewTab companyProfile={companyProfile} />,
    })

    // Services & Expertise
    // --------------------
    // We only want to show this tab if the company is registered on RQ
    // and has services
    if (companyProfile.isRegistered && companyProfile.hasServices) {
      tabs.push({
        title: 'Services & Expertise',
        key: COMPANY_PROFILE_TAB.servicesAndExpertise,
        content: (
          <ServicesAndExpertise
            company={companyProfile.company}
            companyServices={companyProfile.company?.services ?? []}
            expertiseAreas={companyProfile?.company?.expertiseAreas ?? []}
            fcaServices={companyProfile?.directory?.fca?.services ?? []}
          />
        ),
      })
    }

    // Commercial agreements
    const shouldShowCommercialAgreementsTab =
      company &&
      panel &&
      panel.status !== PANEL_STATUS.rejected &&
      !panel.internalRelationship

    if (shouldShowCommercialAgreementsTab) {
      const pendingActions = panel.pendingActions

      const needsToReview =
        pendingActions.includes(PENDING_PANEL_ACTION.reviewCurrentAgreement) ||
        pendingActions.includes(PENDING_PANEL_ACTION.reviewPendingAgreement)

      const needsToAddCommercialAgreement = pendingActions.includes(
        PENDING_PANEL_ACTION.addCommercialAgreement,
      )

      const needsToChaseCommercialAgreementReview =
        pendingActions.includes(
          PENDING_PANEL_ACTION.chaseCurrentAgreementReview,
        ) ||
        pendingActions.includes(
          PENDING_PANEL_ACTION.chasePendingAgreementReview,
        )

      const renderTooltip = () => {
        if (needsToReview) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-warning"
                />
              </TooltipTrigger>

              <TooltipContent>
                {PANEL_AGREEMENT_MESSAGE.reviewAgreement}
              </TooltipContent>
            </Tooltip>
          )
        }

        if (needsToChaseCommercialAgreementReview) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-warning"
                />
              </TooltipTrigger>

              <TooltipContent>
                {PANEL_AGREEMENT_MESSAGE.chaseAgreementReview}
              </TooltipContent>
            </Tooltip>
          )
        }

        if (needsToAddCommercialAgreement) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-warning"
                />
              </TooltipTrigger>
              <TooltipContent>No commercial agreements added</TooltipContent>
            </Tooltip>
          )
        }
      }

      tabs.push({
        title: (
          <div className="flex items-center gap-2">
            <div>Commercial agreements</div>
            {renderTooltip()}
          </div>
        ),
        key: COMPANY_PROFILE_TAB.commercialAgreements,
        content: (
          <Suspense fallback={<SkeletonBox style={{ height: '52vh' }} />}>
            <CommercialAgreementsTab panelId={panel.id} />
          </Suspense>
        ),
      })
    }

    // Team
    if (companyProfile.fcaIndividuals.length > 0) {
      tabs.push({
        title: 'Team',
        key: COMPANY_PROFILE_TAB.team,
        content: <TeamTab fcaIndividuals={companyProfile.fcaIndividuals} />,
      })
    }

    // Reviews
    if (company && totalReviewsCount > 0) {
      tabs.push({
        title: `Reviews (${totalReviewsCount})`,
        key: COMPANY_PROFILE_TAB.reviews,
        content: <ReviewsTab companyId={company.id} />,
      })
    }

    // Monitoring
    if (directory && directory?.monitoringState !== null) {
      const getMonitoringStyles = () => {
        const hasRedFlag = directory.monitoringRedFlags.length > 0

        if (hasRedFlag) {
          return { icon: faTimesCircle, className: 'text-destructive' }
        }

        return directory.monitoringState
          ? { icon: faCheckCircle, className: 'text-success' }
          : { icon: faQuestionCircle, className: 'text-warning' }
      }

      const monitoringStyles = getMonitoringStyles()

      tabs.push({
        title: (
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={monitoringStyles.icon}
              className={cn('mr-2', monitoringStyles.className)}
            />
            Monitoring
          </div>
        ),
        key: COMPANY_PROFILE_TAB.monitoring,
        content: <MonitoringTab fca={directory} />,
      })
    }

    // Calendar links
    if (panel?.canCreateReferralLink && company) {
      tabs.push({
        title: (
          <div className="flex items-center gap-2">
            {company.hasReferralLinkIssue && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="text-destructive"
                  />
                </TooltipTrigger>

                <TooltipContent>
                  Some users of {company.presentationName} have disconnected
                  their calendars so some of your calendar links will not work
                  as expected.
                </TooltipContent>
              </Tooltip>
            )}
            Calendar links
          </div>
        ),
        key: COMPANY_PROFILE_TAB.calendarLinks,
        content: (
          <Suspense fallback={<SkeletonBox style={{ height: '200px' }} />}>
            <CalendarLinksTab company={company} />
          </Suspense>
        ),
      })
    }

    // Your Comments
    if (panel && panel.status !== PANEL_STATUS.rejected) {
      tabs.push({
        title: 'Your Comments',
        key: COMPANY_PROFILE_TAB.yourComments,
        content: (
          <CommentList
            entityAlias={COMMENT_ENTITY_ALIAS.PANEL}
            entityId={panel.id}
          />
        ),
      })
    }

    return tabs
  }

  function renderTabs() {
    return (
      <Tabs value={tabFromSearchParam} onValueChange={handleTabChange}>
        <ScrollArea>
          <TabsList variant="line">
            {tabs.map((tab) => (
              <TabsTrigger key={tab.key} value={tab.key}>
                {tab.title}
              </TabsTrigger>
            ))}
          </TabsList>

          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {tabs.map((tab) => (
          <TabsContent key={tab.key} value={tab.key} className="py-2">
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    )
  }

  function renderNoDataAvailableMessage() {
    return (
      <Card className="mb-6">
        <CardContent>
          <p className="mb-4">
            There isn't any data available for this company.
          </p>

          <Button asChild>
            <Link to={ROUTES.professionalNetworkInvite()}>
              Invite them to join you on RQ
            </Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  const tabs = buildTabs()

  useMount(() => {
    const tab = determineInitialTab()
    searchParams.set(SEARCH_PARAMS.companyProfile.profileTab, tab)
    setSearchParams(searchParams, { replace: true })
  })

  function handleTabChange(tab: string) {
    searchParams.set(SEARCH_PARAMS.companyProfile.profileTab, tab)
    setSearchParams(searchParams)
  }

  const companyName =
    directory?.name ?? company?.name ?? companiesHouse?.name ?? 'Unknown'

  return (
    <>
      <Helmet title={companyName} />

      <PageContainer>
        {company?.videoUrl && (
          <VideoEmbed
            className="mb-4"
            videoUrl={company.videoUrl}
            height={360}
          />
        )}

        <div className="mb-4">
          <CompanyProfileHeader companyProfile={companyProfile} />
        </div>

        {tabs.length > 0 ? renderTabs() : renderNoDataAvailableMessage()}
      </PageContainer>

      <DevOnly>
        <DevActions />
      </DevOnly>
    </>
  )
}

export default CompanyProfile
